import { useState } from "react";
import "./mutual-fund-sections.css";

const CATEGORIES = [
  {
    id: "equity",
    name: "Equity Funds",
    tagline: "High growth potential",
    icon: "fa-chart-line",
    description:
      "Equity funds invest primarily in stocks and aim to generate higher returns over the long term.",
    risk: "High",
    bestFor: "Long-term wealth creation",
    horizon: "5+ Years",
    returns: "High",
    benefits: [
      "High growth potential over the long term",
      "Invest in diversified portfolio of quality stocks",
      "Professional management & research",
      "Compounding helps build significant wealth",
    ],
    image:
      "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "debt",
    name: "Debt Funds",
    tagline: "Stable & regular income",
    icon: "fa-building-columns",
    description:
      "Debt funds invest in fixed-income securities like bonds and money market instruments for relatively stable returns.",
    risk: "Low to Moderate",
    bestFor: "Capital preservation & income",
    horizon: "1–3 Years",
    returns: "Moderate",
    benefits: [
      "Relatively stable and predictable returns",
      "Lower volatility compared to equity funds",
      "Suitable for short to medium-term goals",
      "Better tax efficiency than traditional FDs in many cases",
    ],
    image:
      "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "hybrid",
    name: "Hybrid Funds",
    tagline: "Balance of growth & stability",
    icon: "fa-scale-balanced",
    description:
      "Hybrid funds combine equity and debt to balance growth potential with stability based on your risk profile.",
    risk: "Moderate",
    bestFor: "Balanced investors",
    horizon: "3–5 Years",
    returns: "Moderate to High",
    benefits: [
      "Diversification across equity and debt",
      "Smoother ride through market cycles",
      "Flexible allocation strategies",
      "Ideal for first-time and moderate-risk investors",
    ],
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "index",
    name: "Index Funds",
    tagline: "Low-cost passive investing",
    icon: "fa-chart-simple",
    description:
      "Index funds passively track market indices, offering diversified exposure at lower expense ratios.",
    risk: "Moderate",
    bestFor: "Long-term passive investors",
    horizon: "5+ Years",
    returns: "Market-linked",
    benefits: [
      "Low expense ratios",
      "Broad market diversification",
      "Transparent and rule-based investing",
      "Ideal for SIP-based long-term wealth creation",
    ],
    image:
      "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "elss",
    name: "ELSS Funds",
    tagline: "Tax saving with wealth creation",
    icon: "fa-piggy-bank",
    description:
      "ELSS funds offer tax benefits under Section 80C while investing in equities for long-term wealth creation.",
    risk: "Moderate to High",
    bestFor: "Tax planning + growth",
    horizon: "3+ Years (lock-in)",
    returns: "High",
    benefits: [
      "Tax deduction up to ₹1.5 lakh under 80C",
      "Shortest lock-in among 80C options (3 years)",
      "Equity-oriented growth potential",
      "Builds long-term investment discipline",
    ],
    image:
      "https://images.unsplash.com/photo-1553729459-efe14ef6055d?auto=format&fit=crop&w=800&q=80",
  },
];

const STATS = [
  { key: "risk", label: "Risk Level", icon: "fa-gauge-high" },
  { key: "bestFor", label: "Best For", icon: "fa-bullseye" },
  { key: "horizon", label: "Investment Horizon", icon: "fa-clock" },
  { key: "returns", label: "Potential Returns", icon: "fa-chart-column" },
];

export default function MutualFundCategories() {
  const [activeId, setActiveId] = useState(CATEGORIES[0].id);
  const active = CATEGORIES.find((c) => c.id === activeId) || CATEGORIES[0];

  return (
    <section className="mf-cats" id="categories">
      <div className="mf-cats-inner">
        <header className="mf-sec-header">
          <p className="mf-sec-eyebrow">INVESTMENT SOLUTIONS</p>
          <h2 className="mf-sec-title">Explore Mutual Fund Categories</h2>
        </header>

        <div className="mf-cats-panel">
          <aside className="mf-cats-nav" role="tablist" aria-label="Fund categories">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={activeId === cat.id}
                className={`mf-cats-tab ${activeId === cat.id ? "active" : ""}`}
                onClick={() => setActiveId(cat.id)}
              >
                <span className="mf-cats-tab-icon" aria-hidden="true">
                  <i className={`fa-solid ${cat.icon}`} />
                </span>
                <span className="mf-cats-tab-text">
                  <strong>{cat.name}</strong>
                  <small>{cat.tagline}</small>
                </span>
              </button>
            ))}
          </aside>

          <div className="mf-cats-content" role="tabpanel">
            <p className="mf-cats-desc">{active.description}</p>

            <div className="mf-cats-stats">
              {STATS.map((s) => (
                <div key={s.key} className="mf-cats-stat">
                  <div className="mf-cats-stat-icon" aria-hidden="true">
                    <i className={`fa-solid ${s.icon}`} />
                  </div>
                  <div>
                    <span className="mf-cats-stat-label">{s.label}</span>
                    <strong>{active[s.key]}</strong>
                  </div>
                </div>
              ))}
            </div>

            <div className="mf-cats-benefits-card">
              <div className="mf-cats-benefits">
                <h3>Key Benefits</h3>
                <ul>
                  {active.benefits.map((b) => (
                    <li key={b}>
                      <span className="check" aria-hidden="true">
                        <i className="fa-solid fa-check" />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mf-cats-image">
                <img src={active.image} alt="" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
